import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ClockIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  InformationCircleIcon,
  ClipboardDocumentIcon,
  UserIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import { useStudioPlan } from "../hooks/useStudioPlan";
import Notification from "../components/Notification";
import RegistrationDetailsPanel from "../components/RegistrationDetailsPanel";
import {
  getExtraRegistrationColumns,
  getRegistrationFieldLabel,
  getRegistrationFieldValue,
} from "../utils/registrationFields";
import { exportRegistrationsToExcel } from "../utils/exportRegistrationsToExcel";
import Tooltip from "../components/Tooltip";

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  searchTerm: 100,
  className: 200,
  timeInput: 8, // HH:MM:SS (HTML time inputs can include seconds)
  eventName: 200,
  registrationTitle: 200,
  registrationBody: 2000,
  customTermsConditions: 5000,
  dateInput: 10, // MM/DD/YYYY
  currencyInput: 10, // $999.99
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';

  let sanitized = input;

  if (
    fieldType === 'searchTerm' ||
    fieldType === 'className' ||
    fieldType === 'eventName' ||
    fieldType === 'registrationTitle' ||
    fieldType === 'registrationBody' ||
    fieldType === 'customTermsConditions'
  ) {
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
  } else if (fieldType === 'timeInput') {
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
  } else {
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }

  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;

  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }

  switch (type) {
    case 'searchTerm':
    case 'className':
    case 'eventName':
    case 'registrationTitle':
    case 'registrationBody':
    case 'customTermsConditions':
      const textDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !textDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'timeInput':
      const timeRegex = /^\d{1,2}:\d{2}(:\d{2})?$/;
      return timeRegex.test(sanitized);
    case 'dateInput':
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      return dateRegex.test(sanitized);
    case 'currencyInput':
      const currencyRegex = /^\d*\.?\d{0,2}$/;
      return currencyRegex.test(sanitized);
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Classes Security Event: ${event}`, {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      details
    });
  }
};

// Date input formatter function
const formatDateInput = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  const limited = numbers.slice(0, 8);
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  return '';
};

// Convert database date format to MM/DD/YYYY
const convertDatabaseDateToDisplay = (dateString: string | null): string => {
  if (!dateString) return '';

  try {
    if (dateString.includes('T')) {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } else {
      const [year, month, day] = dateString.split('-').map(Number);
      if (!year || !month || !day) return '';
      return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
    }
  } catch (error) {
    return '';
  }
};

// Convert MM/DD/YYYY format to YYYY-MM-DD for database
const convertDisplayDateToDatabase = (dateString: string): string | null => {
  if (!dateString || dateString.trim() === '') return null;

  try {
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0]) - 1;
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    const date = new Date(year, month, day, 0, 0, 0, 0);
    if (isNaN(date.getTime())) return null;
    const yearStr = date.getFullYear().toString();
    const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = date.getDate().toString().padStart(2, '0');
    return `${yearStr}-${monthStr}-${dayStr}`;
  } catch (error) {
    return null;
  }
};

// Parse currency input to number
const parseCurrencyInput = (value: string): number => {
  const cleanValue = value.replace(/[^\d.]/g, '');
  const num = parseFloat(cleanValue);
  return isNaN(num) ? 0 : num;
};

const defaultRegistrationFields = [
  { id: "first_name", label: "First Name", required: true },
  { id: "last_name", label: "Last Name", required: true },
  { id: "email", label: "Email Address", required: true },
  { id: "phone", label: "Phone Number", required: true },
  { id: "date_of_birth", label: "Date of Birth", required: false },
  { id: "address", label: "Address", required: false },
  { id: "emergency_contact", label: "Emergency Contact", required: false },
  { id: "allergies", label: "Allergies/Medical Information", required: false },
  { id: "medical_info", label: "Medical Information", required: false },
  { id: "experience_level", label: "Martial Arts Experience", required: false },
  { id: "belt_rank", label: "Current Belt Rank", required: false },
  { id: "belt_size", label: "Belt Size", required: false },
  { id: "uniform_size", label: "Uniform Size", required: false },
  { id: "t_shirt_size", label: "T-Shirt Size", required: false },
  { id: "how_did_you_hear", label: "How Did You Hear About Us?", required: false },
  { id: "parent_name", label: "Parent/Guardian Name", required: false },
  { id: "parent_phone", label: "Parent/Guardian Phone", required: false },
  { id: "parent_email", label: "Parent/Guardian Email", required: false },
  { id: "terms_conditions", label: "Terms & Conditions", required: false },
];

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** Local YYYY-MM-DD for calendar bucketing (not UTC). */
const dateToYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Time-of-day display for weekly grid (pure; safe to use outside component). */
const formatScheduleTime = (time: string): string => {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const date = new Date();
  date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

interface ClassInstructor {
  id: string;
  name: string;
  belt: string;
  role: string;
}

interface ClassResponse {
  id: string;
  created_at: string;
  updated_at: string;
  studio_id: string;
  class_name: string;
  assigned_instructors: ClassInstructor[];
  start_time: string;
  end_time: string;
  type: string;
  days: string[];
  repeats_weekly: boolean;
  class_date: string | null;
  age_range: string;
  is_public: boolean;
}

interface FormData {
  studio_id: string;
  class_name: string;
  assigned_instructors: string[];
  start_time: string;
  end_time: string;
  type: string;
  days: string[];
  repeats_weekly: boolean;
  class_date: string;
  age_range: string;
  is_public: boolean;
}

interface StudioEvent {
  id: string;
  studio_id: string;
  event_name: string;
  assigned_instructors: ClassInstructor[];
  start_time: string;
  end_time: string;
  type: string;
  from_date: string;
  end_date: string;
  charge_for_event?: boolean;
  charge_amount?: number;
  create_registration_link?: boolean;
  registration_link?: string;
  registration_fields?: string[];
  auto_promote_after_testing?: boolean;
  registration_title?: string;
  registration_body?: string;
  registration_image?: string;
  custom_terms_conditions?: string;
  show_studio_address_on_registration?: boolean;
}

export default function Classes() {
  const { user, session, getAuthToken } = useAuth();
  const { studioUser, loading: studioUserLoading } = useStudioUser();
  const { plan, hasCapability } = useStudioPlan();
  const recurringClassLimit = plan?.capabilities.maxRecurringClasses ?? null;
  const showAppointmentBooking =
    hasCapability('leadAutomation');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassResponse | null>(null);
  const [availableInstructors, setAvailableInstructors] = useState<
    ClassInstructor[]
  >([]);
  const [formData, setFormData] = useState<FormData>({
    studio_id: "",
    class_name: "",
    assigned_instructors: [],
    start_time: "",
    end_time: "",
    type: "Regular",
    days: [],
    repeats_weekly: true,
    class_date: "",
    age_range: "",
    is_public: true
  });
  const [classToDelete, setClassToDelete] = useState<ClassResponse | null>(
    null
  );
  const [classes, setClasses] = useState<ClassResponse[]>([]);
  const recurringClassCount = useMemo(
    () => classes.filter((c) => c.repeats_weekly).length,
    [classes]
  );
  const recurringLimitReached =
    recurringClassLimit != null && recurringClassCount >= recurringClassLimit;
  const [classesLoading, setClassesLoading] = useState(true);
  const [calWeekStart, setCalWeekStart] = useState<Date>(() => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  // Auto appointment SMS setting
  const [autoAppointmentSmsEnabled, setAutoAppointmentSmsEnabled] = useState<boolean>(true);

  // Event state
  const [events, setEvents] = useState<StudioEvent[]>([]);
  const [editingEvent, setEditingEvent] = useState<StudioEvent | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<StudioEvent | null>(null);
  const [eventFormData, setEventFormData] = useState<Omit<StudioEvent, 'id'>>({
    studio_id: "",
    event_name: "",
    assigned_instructors: [],
    start_time: "",
    end_time: "",
    type: "Regular",
    from_date: "",
    end_date: "",
    charge_for_event: false,
    charge_amount: 0,
    create_registration_link: false,
    registration_link: "",
    custom_terms_conditions: "",
    registration_fields: ["first_name", "last_name", "email", "phone"],
    auto_promote_after_testing: false,
    registration_title: "",
    registration_body: "",
    registration_image: "",
    show_studio_address_on_registration: false,
  });
  const [chargeAmountInput, setChargeAmountInput] = useState("");
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [lastEventSubmissionTime, setLastEventSubmissionTime] = useState(0);
  const [eventCopyStatus, setEventCopyStatus] = useState("");
  const [eventImageFile, setEventImageFile] = useState<File | null>(null);
  const [eventImagePreview, setEventImagePreview] = useState<string>("");

  // Registrations state
  const [viewingRegistrations, setViewingRegistrations] = useState<StudioEvent | null>(null);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<any[]>([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [registrationSearchTerm, setRegistrationSearchTerm] = useState("");
  const [registrationToDelete, setRegistrationToDelete] = useState<any | null>(null);
  const [selectedRegistrationDetails, setSelectedRegistrationDetails] = useState<any | null>(null);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        // eslint-disable-next-line no-console
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  const loadData = useCallback(async () => {
    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    setClassesLoading(true);

    try {
      // Load classes, instructors, events in parallel
      const [classesResponse, instructorsResponse, eventsResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes/${studioUser.studio_id}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/instructors/${studioUser.studio_id}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${studioUser.studio_id}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
      ]);

      if (classesResponse.ok && instructorsResponse.ok) {
        const classesData = await classesResponse.json();
        const instructorsData = await instructorsResponse.json();

        const hydratedClasses = Array.isArray(classesData)
          ? classesData.map((classObj) => ({
              ...classObj,
              assigned_instructors: Array.isArray(classObj.assigned_instructors)
                ? classObj.assigned_instructors
                    .map((id: string) =>
                      instructorsData.find(
                        (inst: ClassInstructor) => inst.id === id
                      )
                    )
                    .filter(Boolean)
                : [],
            }))
          : [];

        setAvailableInstructors(instructorsData);
        setClasses(hydratedClasses);

        // Process events
        if (eventsResponse.ok) {
          const eventsData = await eventsResponse.json();
          const hydratedEvents = Array.isArray(eventsData)
            ? eventsData.map((eventObj) => ({
                ...eventObj,
                assigned_instructors: Array.isArray(eventObj.assigned_instructors)
                  ? eventObj.assigned_instructors
                      .map((id: string) =>
                        instructorsData.find(
                          (inst: ClassInstructor) => inst.id === id
                        )
                      )
                      .filter(Boolean)
                  : [],
              }))
            : [];
          setEvents(hydratedEvents);
        }
      } else {
        throw new Error("Failed to fetch classes or instructors");
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load classes",
        title: "Error",
      });
    } finally {
      setClassesLoading(false);
    }

    // Fetch auto appointment SMS setting and Stripe status
    try {
      const authToken2 = getAuthToken();
      if (!authToken2) return;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        { headers: { Authorization: `Bearer ${authToken2}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAutoAppointmentSmsEnabled(data?.auto_appointment_sms_enabled ?? true);
        setIsStripeConnected(!!data?.stripe_user_id);
      }
    } catch (error) {
      setAutoAppointmentSmsEnabled(true);
    }
  }, [studioUser?.studio_id, getAuthToken]);

  useEffect(() => {
    fetchCsrfToken();
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Bucket classes/events per calendar column only when the week or data changes —
  // avoids re-filtering hundreds of rows on every keystroke in modals.
  const weeklySchedule = useMemo(() => {
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(calWeekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
    const todayStr = dateToYmd(new Date());
    const weekLabel = `${weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

    const columns = weekDays.map((day, i) => {
      const dayName = days[i];
      const ds = dateToYmd(day);
      const dayClasses = classes
        .filter((c) => {
          if (c.repeats_weekly) return Array.isArray(c.days) && c.days.includes(dayName);
          return c.class_date === ds;
        })
        .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

      const dayEvents = events
        .filter((ev) => {
          if (!ev.from_date || !ev.end_date) return false;
          const fromStr = ev.from_date.includes("T")
            ? ev.from_date.split("T")[0]
            : ev.from_date;
          const endStr = ev.end_date.includes("T")
            ? ev.end_date.split("T")[0]
            : ev.end_date;
          return ds >= fromStr && ds <= endStr;
        })
        .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

      return {
        day,
        ds,
        dayClasses,
        dayEvents,
        isToday: ds === todayStr,
      };
    });

    return { weekLabel, columns };
  }, [calWeekStart, classes, events]);

  const handleEditClass = (classItem: ClassResponse) => {
    setFormData({
      studio_id: classItem.studio_id,
      class_name: classItem.class_name,
      assigned_instructors: classItem.assigned_instructors.map((i) => i.id),
      start_time: classItem.start_time,
      end_time: classItem.end_time,
      type: classItem.type,
      days: classItem.days,
      repeats_weekly: classItem.repeats_weekly,
      class_date: classItem.class_date ?? "",
      age_range: classItem.age_range || "",
      is_public: classItem.is_public,
    });
    setEditingClass(classItem);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingClass(null);
    setFormData({
      studio_id: "",
      class_name: "",
      assigned_instructors: [],
      start_time: "",
      end_time: "",
      type: "Regular",
      days: [],
      repeats_weekly: true,
      class_date: "",
      age_range: "",
      is_public: showAppointmentBooking,
    });
  };

  // LeadManagement-style: update state directly on change; sanitize on submit only.
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    let newValue = value;
    if (name === "class_name" && newValue.length > MAX_LENGTHS.className) {
      newValue = newValue.substring(0, MAX_LENGTHS.className);
    } else if (
      (name === "start_time" || name === "end_time") &&
      newValue.length > MAX_LENGTHS.timeInput
    ) {
      newValue = newValue.substring(0, MAX_LENGTHS.timeInput);
    }

    if (name === "days") {
      const daysArray = value.split(",").filter(Boolean);
      setFormData((prev) => ({ ...prev, days: daysArray }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
    }
  };

  const handleDeleteClass = async (classItem: ClassResponse) => {
    setClassToDelete(classItem);
  };

  const handleDeleteAllInstances = async (classItem: ClassResponse) => {
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before deleting another class",
        title: "Rate Limited",
      });
      return;
    }

    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteClass' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with deletion');
      }
    }

    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes/${classItem.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete class");
      }

      await loadData();
      setClassToDelete(null);
      handleModalClose();
      setNotification({
        show: true,
        message: "Class deleted successfully",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('DELETE_CLASS_ERROR', {
        error: error instanceof Error ? error.message : 'Unknown error',
        classId: classItem?.id,
        className: classItem?.class_name
      });
      setNotification({
        show: true,
        message: "Failed to delete class",
        title: "Error",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const authToken = getAuthToken();
    if (!user || !studioUser?.studio_id || !authToken) return;

    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before saving the class again",
        title: "Rate Limited",
      });
      return;
    }

    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'saveClass' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    if (!formData.class_name || !validateInput(formData.class_name, 'className')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'className', value: formData.class_name });
      setNotification({
        show: true,
        message: "Please enter a valid class name",
        title: "Validation Error",
      });
      return;
    }

    if (!formData.start_time || !validateInput(formData.start_time, 'timeInput')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'start_time', value: formData.start_time, type: typeof formData.start_time });
      setNotification({
        show: true,
        message: "Please enter a valid start time (HH:MM)",
        title: "Validation Error",
      });
      return;
    }

    if (!formData.end_time || !validateInput(formData.end_time, 'timeInput')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'end_time', value: formData.end_time, type: typeof formData.end_time });
      setNotification({
        show: true,
        message: "Please enter a valid end time (HH:MM)",
        title: "Validation Error",
      });
      return;
    }

    if (!formData.repeats_weekly && !formData.class_date) {
      setNotification({
        show: true,
        message: "Please select a class date for one-time classes.",
        title: "Validation Error",
      });
      return;
    }

    if (
      formData.repeats_weekly &&
      recurringClassLimit != null &&
      (!editingClass || !editingClass.repeats_weekly) &&
      recurringClassCount >= recurringClassLimit
    ) {
      setNotification({
        show: true,
        message: `Your plan allows up to ${recurringClassLimit} recurring classes. Delete an existing recurring class or create a one-time class instead.`,
        title: "Plan Limit",
      });
      return;
    }

    setLastSubmissionTime(now);

    try {
      const url = editingClass
        ? `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes/${editingClass.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes`;

      const response = await fetch(url, {
        method: editingClass ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          ...formData,
          studio_id: studioUser.studio_id,
          class_name: sanitizeInput(formData.class_name, "className"),
          start_time: sanitizeInput(formData.start_time, "timeInput"),
          end_time: sanitizeInput(formData.end_time, "timeInput"),
          age_range: showAppointmentBooking
            ? sanitizeInput(formData.age_range || "", undefined)
            : "",
          is_public: showAppointmentBooking ? formData.is_public : false,
          class_date: formData.class_date
            ? sanitizeInput(formData.class_date, undefined)
            : null,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save class");
      }

      await response.json();
      await loadData();
      handleModalClose();

      setNotification({
        show: true,
        message: editingClass ? "Class updated successfully" : "Class added successfully",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('SAVE_CLASS_ERROR', {
        error: error instanceof Error ? error.message : 'Unknown error',
        className: formData.class_name,
        isEditing: !!editingClass
      });
      setNotification({
        show: true,
        message: "Failed to save class",
        title: "Error",
      });
    }
  };

  const handleDaysChange = (day: string) => {
    setFormData((prev) => {
      const days = prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day];
      return { ...prev, days };
    });
  };

  // Utility function for consistent input styling
  const getInputClassName = (baseClasses: string) => {
    return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600`;
  };

  // ---- Event handlers ----

  const handleEditEvent = (event: StudioEvent) => {
    const { id, ...eventData } = event;
    const formattedEventData = {
      ...eventData,
      from_date: eventData.from_date ? eventData.from_date.split('T')[0] : '',
      end_date: eventData.end_date ? eventData.end_date.split('T')[0] : '',
    };
    setEventFormData(formattedEventData);
    setEditingEvent(event);
    setChargeAmountInput(
      typeof event.charge_amount === "number" && !isNaN(event.charge_amount)
        ? String(event.charge_amount)
        : ""
    );
    setEventImagePreview(event.registration_image || "");
    setEventImageFile(null);
    setIsEventModalOpen(true);
  };

  const handleEventModalClose = () => {
    setIsEventModalOpen(false);
    setEditingEvent(null);
    setChargeAmountInput("");
    setEventImagePreview("");
    setEventImageFile(null);
    setEventFormData({
      studio_id: "",
      event_name: "",
      assigned_instructors: [],
      start_time: "",
      end_time: "",
      type: "Regular",
      from_date: "",
      end_date: "",
      charge_for_event: false,
      charge_amount: 0,
      create_registration_link: false,
      registration_link: "",
      custom_terms_conditions: "",
      registration_fields: ["first_name", "last_name", "email", "phone"],
      auto_promote_after_testing: false,
      registration_title: "",
      registration_body: "",
      registration_image: "",
      show_studio_address_on_registration: false,
    });
  };

  // LeadManagement-style: direct state updates; sanitize string fields in handleSaveEvent.
  const handleEventInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    let newValue = value;
    if (name === "event_name" && newValue.length > MAX_LENGTHS.eventName) {
      newValue = newValue.substring(0, MAX_LENGTHS.eventName);
    } else if (
      (name === "start_time" || name === "end_time") &&
      newValue.length > MAX_LENGTHS.timeInput
    ) {
      newValue = newValue.substring(0, MAX_LENGTHS.timeInput);
    } else if (name === "registration_title" && newValue.length > MAX_LENGTHS.registrationTitle) {
      newValue = newValue.substring(0, MAX_LENGTHS.registrationTitle);
    } else if (name === "registration_body" && newValue.length > MAX_LENGTHS.registrationBody) {
      newValue = newValue.substring(0, MAX_LENGTHS.registrationBody);
    } else if (name === "custom_terms_conditions" && newValue.length > MAX_LENGTHS.customTermsConditions) {
      newValue = newValue.substring(0, MAX_LENGTHS.customTermsConditions);
    } else if (name === "charge_amount" && newValue.length > MAX_LENGTHS.currencyInput) {
      newValue = newValue.substring(0, MAX_LENGTHS.currencyInput);
    }

    if (name === "from_date" || name === "end_date") {
      setEventFormData((prev) => ({ ...prev, [name]: newValue }));
    } else if (name === "create_registration_link") {
      setEventFormData((prev) => ({ ...prev, [name]: value === "true" }));
    } else if (name === "auto_promote_after_testing") {
      setEventFormData((prev) => ({ ...prev, [name]: value === "true" }));
    } else {
      setEventFormData((prev) => ({ ...prev, [name]: newValue }));
    }
  };

  const compressEventImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        const maxSize = 400;
        let { width, height } = img;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = objectUrl;
    });
  };

  const handleEventImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      setNotification({
        show: true,
        message: "Please select a JPEG, PNG, or WebP file only",
        title: "Invalid File Type",
      });
      e.target.value = "";
      return;
    }

    if (file.size > 1048576) {
      setNotification({
        show: true,
        message: "File size must be less than 1MB",
        title: "File Too Large",
      });
      e.target.value = "";
      return;
    }

    setEventImageFile(file);

    try {
      const compressedBase64 = await compressEventImage(file);
      setEventImagePreview(compressedBase64);
      setEventFormData((prev) => ({
        ...prev,
        registration_image: compressedBase64,
      }));
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to process image. Please try again.",
        title: "Image Processing Error",
      });
    } finally {
      e.target.value = "";
    }
  };

  const removeEventImage = () => {
    setEventImageFile(null);
    setEventImagePreview("");
    setEventFormData((prev) => ({
      ...prev,
      registration_image: "",
    }));
  };

  const handleSaveEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    const authToken = getAuthToken();
    if (!user || !authToken) return;

    const now = Date.now();
    if (now - lastEventSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before saving the event again",
        title: "Rate Limited",
      });
      return;
    }

    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'saveEvent' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    if (!eventFormData.event_name || !validateInput(eventFormData.event_name, 'eventName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'eventName', value: eventFormData.event_name });
      setNotification({
        show: true,
        message: "Please enter a valid event name",
        title: "Validation Error",
      });
      return;
    }

    if (!eventFormData.from_date) {
      setNotification({
        show: true,
        message: "Please enter a valid start date",
        title: "Validation Error",
      });
      return;
    }

    if (!eventFormData.end_date) {
      setNotification({
        show: true,
        message: "Please enter a valid end date",
        title: "Validation Error",
      });
      return;
    }

    if (eventFormData.charge_for_event && !isStripeConnected) {
      setNotification({
        show: true,
        message: "Payment processing requires a connected Stripe account. Please connect your Stripe account in Settings → Integrations to enable payment features.",
        title: "Stripe Required",
      });
      return;
    }

    setLastEventSubmissionTime(now);

    try {
      const studioResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!studioResponse.ok) throw new Error("Failed to fetch studio data");
      const studioData = await studioResponse.json();

      const eventData = {
        ...eventFormData,
        studio_id: studioData.id,
        event_name: sanitizeInput(eventFormData.event_name, "eventName"),
        start_time: sanitizeInput(eventFormData.start_time, "timeInput"),
        end_time: sanitizeInput(eventFormData.end_time, "timeInput"),
        registration_title: sanitizeInput(
          eventFormData.registration_title || "",
          "registrationTitle"
        ),
        registration_body: sanitizeInput(
          eventFormData.registration_body || "",
          "registrationBody"
        ),
        custom_terms_conditions: sanitizeInput(
          eventFormData.custom_terms_conditions || "",
          "customTermsConditions"
        ),
        registration_link: sanitizeInput(
          eventFormData.registration_link || "",
          undefined
        ),
        registration_image: eventFormData.registration_image || "",
        from_date: eventFormData.from_date || new Date().toISOString().split("T")[0],
        end_date: eventFormData.end_date || new Date().toISOString().split("T")[0],
        assigned_instructors: eventFormData.assigned_instructors.map((i) => i.id),
      };

      if (editingEvent) {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${editingEvent.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify(eventData),
          }
        );
        if (!response.ok) throw new Error("Failed to update event");
        setNotification({ show: true, message: "Event updated successfully", title: "Success" });
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify(eventData),
          }
        );
        if (!response.ok) throw new Error("Failed to create event");
        setNotification({ show: true, message: "Event added successfully", title: "Success" });
      }

      handleEventModalClose();
      await loadData();
    } catch (error) {
      logSecurityEvent('SAVE_EVENT_ERROR', {
        error: error instanceof Error ? error.message : 'Unknown error',
        eventName: eventFormData.event_name,
        isEditing: !!editingEvent
      });
      setNotification({ show: true, message: "Failed to save event", title: "Error" });
    }
  };

  const handleDeleteEvent = (event: StudioEvent) => {
    setEventToDelete(event);
  };

  const confirmDeleteEvent = async () => {
    if (!eventToDelete) return;

    const now = Date.now();
    if (now - lastEventSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before deleting another event",
        title: "Rate Limited",
      });
      return;
    }

    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteEvent' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with deletion');
      }
    }

    setLastEventSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${eventToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (
          response.status === 400 &&
          errorData.error === "Cannot delete event with attendance records"
        ) {
          setNotification({
            show: true,
            message: "This event has attendance records and cannot be deleted unless you first delete the attendance records.",
            title: "Cannot Delete Event",
          });
        } else {
          setNotification({
            show: true,
            message: errorData.message || "Failed to delete event",
            title: "Error",
          });
        }
        setEventToDelete(null);
        return;
      }

      setEventToDelete(null);
      handleEventModalClose();
      await loadData();
      setNotification({ show: true, message: "Event deleted successfully", title: "Success" });
    } catch (error) {
      logSecurityEvent('DELETE_EVENT_ERROR', {
        error: error instanceof Error ? error.message : 'Unknown error',
        eventId: eventToDelete?.id,
        eventName: eventToDelete?.event_name
      });
      setNotification({ show: true, message: "Failed to delete event", title: "Error" });
      setEventToDelete(null);
    }
  };

  const cancelDeleteEvent = () => {
    setEventToDelete(null);
  };

  const copyEventRegistrationLink = async (eventId: string) => {
    const link = `${window.location.origin}/register/event/${eventId}`;
    try {
      await navigator.clipboard.writeText(link);
      setEventCopyStatus("Copied!");
      setTimeout(() => setEventCopyStatus(""), 2000);
    } catch (err) {
      setEventCopyStatus("Failed to copy");
      setTimeout(() => setEventCopyStatus(""), 2000);
    }
  };

  const handleViewRegistrations = async (event: StudioEvent) => {
    setViewingRegistrations(event);
    setLoadingRegistrations(true);
    setRegistrationSearchTerm("");
    try {
      const authToken = getAuthToken();
      if (!authToken) return;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${event.id}/registrations`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!response.ok) throw new Error("Failed to fetch registrations");
      const data = await response.json();
      setRegistrations(data);
      setFilteredRegistrations(data);
    } catch (error) {
      setNotification({ show: true, message: "Failed to load registrations", title: "Error" });
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const closeRegistrationsModal = () => {
    setViewingRegistrations(null);
    setRegistrations([]);
    setFilteredRegistrations([]);
    setRegistrationSearchTerm("");
    setRegistrationToDelete(null);
    setSelectedRegistrationDetails(null);
  };

  const handleExportRegistrations = () => {
    if (!viewingRegistrations) return;

    if (registrations.length === 0) {
      setNotification({
        show: true,
        message: "No registrations to export",
        title: "Export",
      });
      return;
    }

    exportRegistrationsToExcel(
      registrations,
      viewingRegistrations.event_name,
      viewingRegistrations.registration_fields
    );

    setNotification({
      show: true,
      message: "Registrations exported successfully",
      title: "Export Complete",
    });
  };

  const handleRegistrationSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRegistrationSearchTerm(value);
    const lower = value.toLowerCase();
    setFilteredRegistrations(
      registrations.filter((reg) => {
        const first = reg.registration_data?.firstName?.toLowerCase() || "";
        const last = reg.registration_data?.lastName?.toLowerCase() || "";
        const email = reg.registration_data?.email?.toLowerCase() || "";
        const phone = reg.registration_data?.phone?.toLowerCase() || "";
        return first.includes(lower) || last.includes(lower) || email.includes(lower) || phone.includes(lower);
      })
    );
  };

  const handleDeleteRegistration = (registration: any) => {
    setRegistrationToDelete(registration);
  };

  const confirmDeleteRegistration = async () => {
    if (!registrationToDelete) return;
    try {
      const authToken = getAuthToken();
      if (!authToken) return;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/event-registrations/${registrationToDelete.id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!response.ok) throw new Error("Failed to delete registration");
      const updated = registrations.filter((r) => r.id !== registrationToDelete.id);
      setRegistrations(updated);
      setFilteredRegistrations(updated);
      setRegistrationToDelete(null);
      setNotification({ show: true, message: "Registration deleted successfully", title: "Success" });
    } catch (error) {
      setNotification({ show: true, message: "Failed to delete registration", title: "Error" });
      setRegistrationToDelete(null);
    }
  };

  const cancelDeleteRegistration = () => {
    setRegistrationToDelete(null);
  };

  const handleRegistrationUpdated = (updated: any) => {
    setSelectedRegistrationDetails(updated);
    setRegistrations((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setFilteredRegistrations((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setNotification({
      show: true,
      message: "Registration updated successfully",
      title: "Success",
    });
  };

  const exportEventData = async (event: StudioEvent) => {
    let registrationsData: any[] = [];
    if (event.create_registration_link) {
      try {
        const authToken = getAuthToken();
        if (authToken) {
          const [registrationsResponse, attendanceResponse] = await Promise.all([
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${event.id}/registrations`,
              { headers: { Authorization: `Bearer ${authToken}` } }
            ),
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/${event.studio_id}?event_id=${event.id}&date=${event.from_date}`,
              { headers: { Authorization: `Bearer ${authToken}` } }
            ),
          ]);
          if (registrationsResponse.ok) {
            const regData = await registrationsResponse.json();
            const attendanceData = attendanceResponse.ok ? await attendanceResponse.json() : [];
            registrationsData = regData.map((registration: any) => ({
              ...registration,
              attendanceStatus: attendanceData.length > 0 ? 'unknown' : 'no-attendance-data',
            }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch registrations for export:', error);
      }
    }

    const exportData = {
      event: {
        id: event.id,
        studio_id: event.studio_id,
        event_name: event.event_name,
        assigned_instructors: event.assigned_instructors,
        start_time: event.start_time,
        end_time: event.end_time,
        type: event.type,
        from_date: event.from_date,
        end_date: event.end_date,
        charge_for_event: event.charge_for_event,
        charge_amount: event.charge_amount,
        create_registration_link: event.create_registration_link,
        registration_link: event.registration_link,
        registration_fields: event.registration_fields,
        auto_promote_after_testing: event.auto_promote_after_testing,
        registration_title: event.registration_title,
        registration_body: event.registration_body,
        registration_image: event.registration_image,
        custom_terms_conditions: event.custom_terms_conditions,
      },
      registrations: registrationsData,
      exported_at: new Date().toISOString(),
      export_metadata: {
        total_registrations: registrationsData.length,
        has_registrations: event.create_registration_link || false,
        registration_link: event.create_registration_link
          ? `${window.location.origin}/register/event/${event.id}`
          : null,
      },
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.event_name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_data_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setNotification({ show: true, message: "Event data exported successfully", title: "Export Complete" });
  };

  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6 min-w-0 max-w-full">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Class & Event Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage your studio classes and events.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsEventModalOpen(true)}
              disabled={studioUserLoading}
              className={`inline-flex items-center rounded-md bg-white dark:bg-gray-800 border border-indigo-300 dark:border-indigo-600 px-3 py-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300 shadow-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-fit xl:w-auto ${
                studioUserLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" aria-hidden="true" />
              <span className="pointer-events-none">Add Event</span>
            </button>
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              disabled={studioUserLoading}
              className={`inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-fit xl:w-auto ${
                studioUserLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" aria-hidden="true" />
              <span className="pointer-events-none">
                Add Class
                {recurringClassLimit != null
                  ? ` ${recurringClassCount}/${recurringClassLimit}`
                  : ""}
              </span>
            </button>
          </div>
        </div>

        {/* Loading */}
        {(classesLoading || studioUserLoading) && (
          <div className="flex justify-center items-center py-12">
            <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
            <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading classes & events...</span>
          </div>
        )}

        {/* Weekly Calendar — unmount while a modal is open so React doesn't reconcile
            the full 7-column grid on every keystroke in form inputs. */}
        {!classesLoading && !studioUserLoading && !isModalOpen && !isEventModalOpen && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 min-w-0 max-w-full">
              {/* Header */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 min-w-0">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white shrink-0">
                  Weekly Schedule
                </h2>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-w-0 w-full sm:w-auto">
                  <div className="flex items-center justify-center gap-2 min-w-0">
                    <button
                      type="button"
                      onClick={() => { const d = new Date(calWeekStart); d.setDate(d.getDate() - 7); setCalWeekStart(d); }}
                      className="p-1 rounded shrink-0 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-600 dark:text-gray-400 min-w-0 flex-1 text-center sm:min-w-[200px] sm:flex-none sm:shrink-0">
                      {weeklySchedule.weekLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => { const d = new Date(calWeekStart); d.setDate(d.getDate() + 7); setCalWeekStart(d); }}
                      className="p-1 rounded shrink-0 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => { const d = new Date(); const dow = d.getDay(); d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1)); d.setHours(0,0,0,0); setCalWeekStart(d); }}
                    className="w-full sm:w-auto px-2 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 self-center sm:self-auto"
                  >
                    Today
                  </button>
                </div>
              </div>

              {/* Grid */}
              <div className="overflow-x-auto">
                <div className="grid grid-cols-7 gap-2 min-w-[700px]">
                  {/* Day headers */}
                  {weeklySchedule.columns.map((col) => (
                      <div key={`head-${col.ds}`} className="text-center pb-2 border-b border-gray-100 dark:border-gray-700">
                        <div className={`text-xs font-semibold uppercase tracking-wide ${col.isToday ? "text-indigo-600 dark:text-indigo-400" : "text-gray-500 dark:text-gray-400"}`}>
                          {col.day.toLocaleDateString("en-US", { weekday: "short" })}
                        </div>
                        <div className={`text-sm font-bold mt-0.5 ${col.isToday ? "text-indigo-600 dark:text-indigo-400" : "text-gray-900 dark:text-white"}`}>
                          {col.day.getDate()}
                        </div>
                      </div>
                    ))}

                  {/* Class and event cards per day */}
                  {weeklySchedule.columns.map((col) => {
                    const hasItems = col.dayClasses.length > 0 || col.dayEvents.length > 0;

                    return (
                      <div key={`col-${col.ds}`} className="min-h-[80px] space-y-1.5 pt-2">
                        {!hasItems ? (
                          <div className="h-full flex items-start justify-center pt-4">
                            <span className="text-xs text-gray-200 dark:text-gray-700">—</span>
                          </div>
                        ) : (
                          <>
                            {col.dayClasses.map((c) => {
                              const type = c.type;
                              const cardBg =
                                type === "Private" ? "bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700"
                                : type === "Special" ? "bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700"
                                : "bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700";
                              const nameColor =
                                type === "Private" ? "text-purple-700 dark:text-purple-300"
                                : type === "Special" ? "text-amber-700 dark:text-amber-300"
                                : "text-blue-700 dark:text-blue-300";
                              const timeColor =
                                type === "Private" ? "text-purple-500 dark:text-purple-400"
                                : type === "Special" ? "text-amber-500 dark:text-amber-400"
                                : "text-blue-500 dark:text-blue-400";
                              const instrColor =
                                type === "Private" ? "text-purple-400 dark:text-purple-500"
                                : type === "Special" ? "text-amber-400 dark:text-amber-500"
                                : "text-blue-400 dark:text-blue-500";
                              const badgeBg =
                                type === "Private" ? "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
                                : type === "Special" ? "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
                                : "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400";
                              return (
                                <div
                                  key={c.id}
                                  className={`rounded-lg px-2 py-1.5 text-left cursor-pointer hover:opacity-80 transition ${cardBg}`}
                                  onClick={() => handleEditClass(c)}
                                >
                                  <div className={`text-xs font-semibold truncate ${nameColor}`}>{c.class_name}</div>
                                  <div className={`text-xs mt-0.5 ${timeColor}`}>
                                    {formatScheduleTime(c.start_time)}–{formatScheduleTime(c.end_time)}
                                  </div>
                                  {Array.isArray(c.assigned_instructors) && c.assigned_instructors.length > 0 && (
                                    <div className={`text-xs truncate mt-0.5 ${instrColor}`}>
                                      {c.assigned_instructors.map((inst: any) => inst?.name).filter(Boolean).join(", ")}
                                    </div>
                                  )}
                                  {c.type && (
                                    <span className={`inline-block text-xs px-1 rounded-full mt-0.5 font-medium ${badgeBg}`}>
                                      {c.type}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                            {col.dayEvents.map((ev) => (
                              <div
                                key={ev.id}
                                className="rounded-lg px-2 py-1.5 text-left cursor-pointer hover:opacity-80 transition bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700"
                                onClick={() => handleEditEvent(ev)}
                              >
                                <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 truncate">{ev.event_name}</div>
                                {ev.start_time && ev.end_time && (
                                  <div className="text-xs text-emerald-500 dark:text-emerald-400 mt-0.5">
                                    {formatScheduleTime(ev.start_time)}–{formatScheduleTime(ev.end_time)}
                                  </div>
                                )}
                                {Array.isArray(ev.assigned_instructors) && ev.assigned_instructors.length > 0 && (
                                  <div className="text-xs text-emerald-400 dark:text-emerald-500 truncate mt-0.5">
                                    {ev.assigned_instructors.map((inst: any) => inst?.name).filter(Boolean).join(", ")}
                                  </div>
                                )}
                                {ev.type && (
                                  <span className="inline-block text-xs px-1 rounded-full mt-0.5 font-medium bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                                    {ev.type}
                                  </span>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Legend */}
              <div className="flex items-center gap-4 mt-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full bg-blue-400 dark:bg-blue-500"></span>
                  Regular Class
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full bg-purple-400 dark:bg-purple-500"></span>
                  Private Class
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full bg-amber-400 dark:bg-amber-500"></span>
                  Special Class
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full bg-emerald-400 dark:bg-emerald-500"></span>
                  Event
                </span>
              </div>
            </div>
        )}

        {/* Modal for adding/editing class */}
        {isModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-lg xl:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={handleModalClose}
                >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left w-full">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      {editingClass ? "Edit Class" : "Add New Class"}
                    </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="class_name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Class Name
                  </label>
                  <input
                    type="text"
                    name="class_name"
                    id="class_name"
                    maxLength={MAX_LENGTHS.className}
                    value={formData.class_name}
                    onChange={handleInputChange}
                    required
                    className={getInputClassName(
                      "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    )}
                  />
                </div>

                {autoAppointmentSmsEnabled && showAppointmentBooking && (
                  <div>
                    <label
                      htmlFor="age_range"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      <Tooltip text="This helps leads choose the right class when booking appointments. If Twilio SMS is enabled, leads will see age ranges in the class selection dropdown.">
                        <span className="cursor-help">
                          Age Range
                        </span>
                      </Tooltip>
                    </label>
                    <input
                      type="text"
                      name="age_range"
                      id="age_range"
                      placeholder="e.g., 5-12, 13-17, 18+, All Ages"
                      value={formData.age_range}
                      onChange={handleInputChange}
                      className={getInputClassName(
                        "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      )}
                    />
                  </div>
                )}

                {autoAppointmentSmsEnabled && showAppointmentBooking && (
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-200 dark:border-indigo-700 rounded-lg p-4 relative">
                    <div className="flex items-start">
                      <div className="flex items-center flex-1">
                        <input
                          type="checkbox"
                          name="is_public"
                          id="is_public"
                          checked={formData.is_public}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
                          className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
                        />
                        <label
                          htmlFor="is_public"
                          className="ml-3 block text-sm font-semibold text-gray-900 dark:text-white"
                        >
                          <Tooltip text="When checked, this class will be available for public appointment booking. Uncheck to hide this class from the appointment booking form.">
                            <span className="cursor-help">
                              Show in Public Appointment Booking
                            </span>
                          </Tooltip>
                        </label>
                      </div>
                      {/* Animated attention arrow */}
                      <div className="ml-3 flex items-center">
                        <div className="animate-pulse">
                          <svg
                            className="w-6 h-6 text-indigo-600 dark:text-indigo-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        <span className="ml-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 animate-pulse">
                          Important!
                        </span>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-indigo-700 dark:text-indigo-300">
                      This must be checked for classes to appear on your public appointment booking link.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="not_recurring"
                      checked={!formData.repeats_weekly}
                      onChange={(e) => setFormData(prev => ({ ...prev, repeats_weekly: !e.target.checked, class_date: "" }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label htmlFor="not_recurring" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                      Not Recurring <span className="text-xs text-gray-400 font-normal">(one-time class)</span>
                    </label>
                  </div>
                  {!formData.repeats_weekly && (
                    <div>
                      <label htmlFor="class_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Class Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        id="class_date"
                        required
                        value={formData.class_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, class_date: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}
                  {formData.repeats_weekly &&
                    recurringLimitReached &&
                    (!editingClass || !editingClass.repeats_weekly) && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Recurring class limit reached ({recurringClassCount}/{recurringClassLimit}). Choose one-time or delete an existing recurring class.
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="type"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Class Type
                  </label>
                  <select
                    name="type"
                    id="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className={getInputClassName(
                      "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    )}
                  >
                    <option value="Regular">Regular</option>
                    <option value="Private">Private</option>
                    <option value="Special">Special</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="start_time"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Start Time
                    </label>
                    <input
                      type="time"
                      name="start_time"
                      id="start_time"
                      maxLength={MAX_LENGTHS.timeInput}
                      value={formData.start_time}
                      onChange={handleInputChange}
                      required
                      className={getInputClassName(
                        "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      )}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="end_time"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      End Time
                    </label>
                    <input
                      type="time"
                      name="end_time"
                      id="end_time"
                      maxLength={MAX_LENGTHS.timeInput}
                      value={formData.end_time}
                      onChange={handleInputChange}
                      required
                      className={getInputClassName(
                        "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      )}
                    />
                  </div>
                </div>

                {formData.repeats_weekly ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Days
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {days.map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleDaysChange(day)}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                            formData.days.includes(day)
                              ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                          }`}
                        >
                          <span className="pointer-events-none">{day}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Instructors
                  </label>
                  <div className="space-y-2">
                    {availableInstructors.map((instructor) => (
                      <label
                        key={instructor.id}
                        className="flex items-center space-x-2"
                      >
                        <input
                          type="checkbox"
                          checked={formData.assigned_instructors.includes(
                            instructor.id
                          )}
                          onChange={(e) => {
                            const newInstructors = e.target.checked
                              ? [
                                  ...formData.assigned_instructors,
                                  instructor.id,
                                ]
                              : formData.assigned_instructors.filter(
                                  (id) => id !== instructor.id
                                );
                            setFormData((prev) => ({
                              ...prev,
                              assigned_instructors: newInstructors,
                            }));
                          }}
                          className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {instructor.name}
                          {instructor.belt ? ` (${instructor.belt})` : ""}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-6 xl:mt-4 flex flex-col xl:flex-row xl:flex-row-reverse gap-3 xl:gap-0">
                  <button
                    type="submit"
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 xl:ml-3 xl:w-auto"
                  >
                    <span className="pointer-events-none">{editingClass ? "Save Changes" : "Add Class"}</span>
                  </button>
                  {editingClass && (
                    <button
                      type="button"
                      onClick={() => handleDeleteClass(editingClass)}
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 xl:ml-3 xl:w-auto"
                    >
                      <TrashIcon className="h-4 w-4 mr-1.5 pointer-events-none" />
                      <span className="pointer-events-none">Delete</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 xl:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation modal for class */}
        {classToDelete && (
          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Delete Class
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Are you sure you want to delete "{classToDelete.class_name}"? This
                action cannot be undone.
              </p>
              <div className="flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                <button
                  onClick={() => setClassToDelete(null)}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  onClick={() => handleDeleteAllInstances(classToDelete)}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <span className="pointer-events-none">Delete</span>
                </button>
              </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Modal */}
        {isEventModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-lg xl:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleEventModalClose}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left w-full">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      {editingEvent ? "Edit Event" : "Add New Event"}
                    </h3>
                    <div className="mt-2">
                      <form className="space-y-4" onSubmit={handleSaveEvent}>
                        <div>
                          <label
                            htmlFor="event_name"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Event Name
                          </label>
                          <input
                            type="text"
                            name="event_name"
                            id="event_name"
                            required
                            maxLength={MAX_LENGTHS.eventName}
                            value={eventFormData.event_name}
                            onChange={handleEventInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="ev_from_date"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            From Date
                          </label>
                          <input
                            type="date"
                            name="from_date"
                            id="ev_from_date"
                            required
                            value={eventFormData.from_date}
                            onChange={handleEventInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="ev_end_date"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            End Date
                          </label>
                          <input
                            type="date"
                            name="end_date"
                            id="ev_end_date"
                            required
                            value={eventFormData.end_date}
                            onChange={handleEventInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="ev_start_time"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Start Time
                          </label>
                          <input
                            type="time"
                            name="start_time"
                            id="ev_start_time"
                            required
                            maxLength={MAX_LENGTHS.timeInput}
                            value={eventFormData.start_time}
                            onChange={handleEventInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="ev_end_time"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            End Time
                          </label>
                          <input
                            type="time"
                            name="end_time"
                            id="ev_end_time"
                            required
                            maxLength={MAX_LENGTHS.timeInput}
                            value={eventFormData.end_time}
                            onChange={handleEventInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="ev_type"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Type
                          </label>
                          <select
                            id="ev_type"
                            name="type"
                            required
                            value={eventFormData.type}
                            onChange={handleEventInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          >
                            <option value="Regular">Regular</option>
                            <option value="Special">Special</option>
                            <option value="Private">Private</option>
                            <option value="Testing">Belt Testing/Promotions</option>
                            <option value="Workshop/Seminar">Workshop/Seminar</option>
                            <option value="Party">Party</option>
                            <option value="Tournament">Tournament</option>
                            <option value="Parent's Night Out">Parent's Night Out</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Instructors
                          </label>
                          <div className="space-y-2">
                            {availableInstructors.map((instructor) => (
                              <label
                                key={instructor.id}
                                className="flex items-center space-x-2"
                              >
                                <input
                                  type="checkbox"
                                  checked={eventFormData.assigned_instructors.some(
                                    (i) => i.id === instructor.id
                                  )}
                                  onChange={(e) => {
                                    const newInstructors = e.target.checked
                                      ? [...eventFormData.assigned_instructors, instructor]
                                      : eventFormData.assigned_instructors.filter(
                                          (i) => i.id !== instructor.id
                                        );
                                    setEventFormData((prev) => ({
                                      ...prev,
                                      assigned_instructors: newInstructors,
                                    }));
                                  }}
                                  className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {instructor.name}
                                  {instructor.belt ? ` (${instructor.belt})` : ""}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Let people register on a public landing page for this event?
                          </label>
                          <div className="space-y-2">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="create_registration_link"
                                value="false"
                                checked={!eventFormData.create_registration_link}
                                onChange={handleEventInputChange}
                                className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">No</span>
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="create_registration_link"
                                value="true"
                                checked={!!eventFormData.create_registration_link}
                                onChange={handleEventInputChange}
                                className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">Yes</span>
                            </label>
                          </div>
                        </div>

                        {eventFormData.create_registration_link && (
                          <>
                            <div>
                              <label
                                htmlFor="ev_registration_title"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                              >
                                Registration Page Title
                              </label>
                              <input
                                type="text"
                                name="registration_title"
                                id="ev_registration_title"
                                maxLength={MAX_LENGTHS.registrationTitle}
                                value={eventFormData.registration_title || ""}
                                onChange={handleEventInputChange}
                                placeholder="Enter a title for the registration page"
                                className={getInputClassName(
                                  "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                )}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="ev_registration_body"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                              >
                                Registration Page Description
                              </label>
                              <textarea
                                name="registration_body"
                                id="ev_registration_body"
                                rows={4}
                                maxLength={MAX_LENGTHS.registrationBody}
                                value={eventFormData.registration_body || ""}
                                onChange={handleEventInputChange}
                                placeholder="Enter description and details about the event..."
                                className={getInputClassName(
                                  "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                )}
                              />
                            </div>
                            <div>
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={eventFormData.show_studio_address_on_registration || false}
                                  onChange={(e) => {
                                    setEventFormData((prev) => ({
                                      ...prev,
                                      show_studio_address_on_registration: e.target.checked,
                                    }));
                                  }}
                                  className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  Show studio address on registration page (replaces event type)
                                </span>
                              </label>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 ml-6">
                                Uses the address from Settings → Profile. If no address is set, the event type will still be shown.
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Event Image
                              </label>
                              <div className="mt-1">
                                {eventImagePreview ? (
                                  <div className="space-y-2">
                                    <img
                                      src={eventImagePreview}
                                      alt="Event preview"
                                      className="mx-auto h-32 w-auto object-contain border border-gray-300 dark:border-gray-600 rounded-md"
                                    />
                                    <div className="flex justify-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={removeEventImage}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                      >
                                        <span className="pointer-events-none">Remove Image</span>
                                      </button>
                                      <label
                                        htmlFor="ev_image-upload-change"
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                                      >
                                        <span className="pointer-events-none">Change Image</span>
                                        <input
                                          id="ev_image-upload-change"
                                          name="ev_image-upload-change"
                                          type="file"
                                          className="sr-only"
                                          accept="image/jpeg,image/jpg,image/png,image/webp"
                                          onChange={handleEventImageUpload}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <label
                                      htmlFor="ev_image-upload"
                                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                                    >
                                      <span className="pointer-events-none">Upload a file</span>
                                      <input
                                        id="ev_image-upload"
                                        name="ev_image-upload"
                                        type="file"
                                        className="sr-only"
                                        accept="image/jpeg,image/jpg,image/png,image/webp"
                                        onChange={handleEventImageUpload}
                                      />
                                    </label>
                                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                      PNG, JPG, JPEG, WebP up to 1MB (will be compressed)
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        {eventFormData.create_registration_link && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Registration Form Fields
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                              Select which fields to include on the registration form
                            </p>
                            <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
                              <p className="text-xs text-blue-800 dark:text-blue-200">
                                <strong>Attendance Tracking:</strong> First Name, Last Name, Email, and Phone Number are used to match registrations with actual attendance records. For accurate attendance tracking, registrants must be existing students or leads in your system.
                              </p>
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-3">
                              {defaultRegistrationFields.map((field) => (
                                <label
                                  key={field.id}
                                  className="flex items-center space-x-2"
                                >
                                  <input
                                    type="checkbox"
                                    checked={eventFormData.registration_fields?.includes(field.id) || field.required}
                                    disabled={field.required}
                                    onChange={(e) => {
                                      const currentFields = eventFormData.registration_fields || [];
                                      const newFields = e.target.checked
                                        ? [...currentFields, field.id]
                                        : currentFields.filter((f) => f !== field.id);
                                      setEventFormData((prev) => ({
                                        ...prev,
                                        registration_fields: newFields,
                                      }));
                                    }}
                                    className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {field.label}
                                    {field.required && (
                                      <span className="text-red-500 ml-1">*</span>
                                    )}
                                  </span>
                                </label>
                              ))}
                              <label className={`flex items-center space-x-2 ${!isStripeConnected ? 'opacity-50' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={!!eventFormData.charge_for_event}
                                  disabled={!isStripeConnected}
                                  onChange={(e) => {
                                    setEventFormData((prev) => ({
                                      ...prev,
                                      charge_for_event: e.target.checked,
                                      charge_amount: e.target.checked ? prev.charge_amount : 0,
                                    }));
                                    setChargeAmountInput((prevInput) => (e.target.checked ? prevInput : ""));
                                  }}
                                  className={`h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 ${!isStripeConnected ? 'cursor-not-allowed' : ''}`}
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  Payment Form
                                  {!isStripeConnected && (
                                    <span className="text-xs text-red-500 ml-1">(Requires Stripe)</span>
                                  )}
                                </span>
                                {!isStripeConnected && (
                                  <div className="ml-2 relative group">
                                    <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal w-64 z-10">
                                      Connect your Stripe account in Settings → Integrations to enable payment processing for event registrations.
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                                    </div>
                                  </div>
                                )}
                                {eventFormData.charge_for_event && (
                                  <div className="flex items-center space-x-2 ml-4">
                                    <input
                                      type="text"
                                      name="charge_amount"
                                      maxLength={MAX_LENGTHS.currencyInput}
                                      value={chargeAmountInput}
                                      onChange={(e) => {
                                        const rawValue = e.target.value;
                                        setChargeAmountInput(rawValue);
                                        const numericValue = parseCurrencyInput(rawValue);
                                        setEventFormData((prev) => ({
                                          ...prev,
                                          charge_amount: numericValue,
                                        }));
                                      }}
                                      className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                                      placeholder="$0.00"
                                    />
                                  </div>
                                )}
                              </label>
                            </div>
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                              * Required fields cannot be removed
                            </p>

                            {/* Custom Terms & Conditions */}
                            {eventFormData.registration_fields?.includes("terms_conditions") && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Custom Terms & Conditions
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                  Customize the terms and conditions text that registrants will see. Leave blank to use the default text.
                                </p>
                                <textarea
                                  name="custom_terms_conditions"
                                  value={eventFormData.custom_terms_conditions || ""}
                                  onChange={handleEventInputChange}
                                  rows={4}
                                  maxLength={MAX_LENGTHS.customTermsConditions}
                                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")}`}
                                  placeholder="I agree to the terms and conditions for this event. By checking this box, I acknowledge that I have read and agree to comply with all event policies, safety guidelines, and studio rules."
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  If left blank, the default terms and conditions will be used.
                                </p>
                                {eventFormData.custom_terms_conditions && (
                                  <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border">
                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Preview:</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      {eventFormData.custom_terms_conditions}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {eventFormData.create_registration_link && (
                          <div>
                            <div className="mt-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Registration Link:
                                </span>
                                <div className="relative group">
                                  <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-80 max-w-xs z-10 whitespace-normal">
                                    <div className="break-words">
                                      Share this link to send people to your public event page (landing content) where they can register.
                                    </div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md p-3">
                                <div className="flex items-center justify-between">
                                  <code className="text-sm text-gray-800 dark:text-gray-200 break-all flex-1 mr-2">
                                    {editingEvent
                                      ? `${window.location.origin}/register/event/${editingEvent.id}`
                                      : `${window.location.origin}/register/event/[event-id]`
                                    }
                                  </code>
                                  {editingEvent && (
                                    <button
                                      type="button"
                                      onClick={() => copyEventRegistrationLink(editingEvent.id)}
                                      className="flex-shrink-0 inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                                    >
                                      <ClipboardDocumentIcon className="h-4 w-4 mr-1 pointer-events-none" />
                                      <span className="pointer-events-none">{eventCopyStatus || "Copy"}</span>
                                    </button>
                                  )}
                                </div>
                              </div>
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                {editingEvent
                                  ? "Copy this link to share the registration page."
                                  : "The link will be generated when you create the event."
                                }
                              </p>
                            </div>
                            {editingEvent && (
                              <div className="mt-3">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleViewRegistrations({
                                      ...editingEvent,
                                      charge_for_event: eventFormData.charge_for_event,
                                      charge_amount: eventFormData.charge_amount,
                                    })
                                  }
                                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border border-indigo-300 dark:border-indigo-600 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                >
                                  <UserIcon className="h-4 w-4 mr-2 pointer-events-none" />
                                  <span className="pointer-events-none">View Registrations</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-6 xl:mt-4 flex flex-col xl:flex-row xl:flex-row-reverse gap-3 xl:gap-0">
                          <button
                            type="submit"
                            className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 xl:ml-3 xl:w-auto"
                          >
                            <span className="pointer-events-none">{editingEvent ? "Update" : "Add Event"}</span>
                          </button>
                          {editingEvent && (
                            <>
                              <button
                                type="button"
                                onClick={() => exportEventData(editingEvent)}
                                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 xl:ml-3 xl:w-auto"
                              >
                                <svg className="h-4 w-4 mr-2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="pointer-events-none">Export Data</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteEvent(editingEvent)}
                                className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 xl:ml-3 xl:w-auto"
                              >
                                <TrashIcon className="h-4 w-4 mr-1.5 pointer-events-none" />
                                <span className="pointer-events-none">Delete</span>
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={handleEventModalClose}
                            className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 xl:w-auto"
                          >
                            <span className="pointer-events-none">Cancel</span>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event delete confirmation modal */}
        {eventToDelete && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Delete Event
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete "{eventToDelete.event_name}"? This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                  <button
                    type="button"
                    onClick={cancelDeleteEvent}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                  <button
                    type="button"
                    onClick={confirmDeleteEvent}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <span className="pointer-events-none">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Registrations Modal */}
        {viewingRegistrations && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6">
                <div className="absolute right-0 top-0 z-10 flex items-center gap-2 p-4">
                  <button
                    type="button"
                    onClick={handleExportRegistrations}
                    disabled={loadingRegistrations || registrations.length === 0}
                    className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5 pointer-events-none" />
                    <span className="pointer-events-none">Export Excel</span>
                  </button>
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={closeRegistrationsModal}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="w-full">
                  <div className="mt-3 pr-44">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Registrations for "{viewingRegistrations.event_name}"
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <UserIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {registrations.length} registrations
                      </span>
                    </div>
                  </div>
                    <div className="mt-4">
                      {!selectedRegistrationDetails && (
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search registrations by name, email, or phone..."
                          value={registrationSearchTerm}
                          onChange={handleRegistrationSearch}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                      )}
                      {!selectedRegistrationDetails && registrationSearchTerm && (
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          Showing {filteredRegistrations.length} of {registrations.length} registrations
                        </p>
                      )}
                    </div>
                    <div className="mt-4">
                      {selectedRegistrationDetails ? (
                        <RegistrationDetailsPanel
                          registration={selectedRegistrationDetails}
                          registrationFields={viewingRegistrations.registration_fields}
                          customTermsConditions={viewingRegistrations.custom_terms_conditions}
                          onBack={() => setSelectedRegistrationDetails(null)}
                          onUpdated={handleRegistrationUpdated}
                          getAuthToken={getAuthToken}
                        />
                      ) : loadingRegistrations ? (
                        <div className="flex justify-center items-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading registrations...</span>
                        </div>
                      ) : registrations.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500 dark:text-gray-400">No registrations found for this event.</p>
                        </div>
                      ) : filteredRegistrations.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500 dark:text-gray-400">No registrations match your search criteria.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          {(() => {
                            const extraColumns = getExtraRegistrationColumns(viewingRegistrations.registration_fields);
                            return (
                          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                            <thead>
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone</th>
                                {extraColumns.map((fieldId) => (
                                  <th
                                    key={fieldId}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                  >
                                    {getRegistrationFieldLabel(fieldId)}
                                  </th>
                                ))}
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Submitted</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {filteredRegistrations.map((registration, index) => (
                                <tr key={registration.id || index}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {registration.registration_data?.firstName} {registration.registration_data?.lastName}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {registration.registration_data?.email}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {registration.registration_data?.phone || '-'}
                                  </td>
                                  {extraColumns.map((fieldId) => (
                                    <td
                                      key={fieldId}
                                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                                    >
                                      {getRegistrationFieldValue(registration.registration_data, fieldId)}
                                    </td>
                                  ))}
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {registration.submitted_at ? new Date(registration.submitted_at).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <div className="flex items-center space-x-1">
                                      <button
                                        type="button"
                                        onClick={() => setSelectedRegistrationDetails(registration)}
                                        className="inline-flex items-center justify-center p-1.5 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors"
                                        title="View registration details"
                                      >
                                        <EyeIcon className="h-5 w-5 pointer-events-none" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteRegistration(registration)}
                                        className="inline-flex items-center justify-center p-1.5 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                        title="Delete registration"
                                      >
                                        <XMarkIcon className="h-5 w-5 pointer-events-none" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeRegistrationsModal}
                    className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Registration Confirmation Modal */}
        {registrationToDelete && (
          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-left sm:ml-4">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">Delete Registration</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete the registration for{" "}
                        <strong>{registrationToDelete.registration_data?.firstName} {registrationToDelete.registration_data?.lastName}</strong>? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={confirmDeleteRegistration}
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Delete</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelDeleteRegistration}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
